import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import CompanyEditor from "@/components/CompanyEditor";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Building2, Copy, Check, Pencil, ExternalLink, Globe2, Mail, MapPin } from "lucide-react";

function CopyField({ label, value, testid }) {
  const [copied, setCopied] = useState(false);
  const doCopy = async () => {
    try {
      await navigator.clipboard.writeText(String(value ?? ""));
      setCopied(true);
      toast.success(`Copied ${label}`);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Copy failed");
    }
  };
  return (
    <div className="group border border-border rounded-xl bg-white p-3.5 flex items-start gap-3 hover:border-emerald-brand/40 transition-colors" data-testid={testid}>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{label}</div>
        <div className="mt-1 text-sm font-medium break-all">{value || <span className="text-muted-foreground">—</span>}</div>
      </div>
      {value && (
        <Button
          size="icon"
          variant="ghost"
          className="h-8 w-8 rounded-lg opacity-70 group-hover:opacity-100"
          onClick={doCopy}
          data-testid={`${testid}-copy`}
          title={copied ? "Copied" : `Copy ${label}`}
        >
          {copied ? <Check className="h-4 w-4 text-emerald-brand" /> : <Copy className="h-4 w-4" />}
        </Button>
      )}
    </div>
  );
}

export default function CompanyDetail() {
  const { id } = useParams();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/companies/${id}`);
      setCompany(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load company");
    }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (loading) return <div className="p-8 text-sm text-muted-foreground">Loading company…</div>;
  if (!company) return <div className="p-8 text-sm text-muted-foreground">Company not found.</div>;

  const cf = company.custom_fields || {};
  const cfKeys = Object.keys(cf);

  return (
    <div className="flex flex-col h-full overflow-y-auto" data-testid="company-detail-page">
      <PageHeader
        title={company.trading_name || company.legal_name}
        subtitle={company.legal_name}
        actions={
          <div className="flex items-center gap-2">
            <Link to="/companies"><Button variant="outline" className="rounded-sm text-xs">All companies</Button></Link>
            <Button className="rounded-sm text-xs" onClick={() => setEditing(true)} data-testid="edit-company-button">
              <Pencil className="h-3.5 w-3.5 mr-1.5" /> Edit
            </Button>
          </div>
        }
      />

      <div className="p-6 lg:p-8 space-y-8">
        {/* Header card */}
        <div className="rounded-2xl border border-border bg-gradient-to-br from-white via-white to-emerald-brand/5 p-5 flex items-start gap-4">
          <div className="h-14 w-14 rounded-2xl bg-emerald-brand/12 text-emerald-brand grid place-items-center shadow-inner">
            <Building2 className="h-7 w-7" strokeWidth={1.6} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="font-display font-semibold text-2xl tracking-tight">{company.trading_name || company.legal_name}</h2>
              <Badge className={`rounded-sm text-[10px] font-mono uppercase ${company.status === "active" ? "bg-emerald-brand/15 text-emerald-brand" : "bg-muted text-muted-foreground"}`}>
                {company.status}
              </Badge>
              <Badge variant="outline" className="rounded-sm text-[10px] font-mono uppercase">{company.tier}</Badge>
            </div>
            <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-xs text-muted-foreground">
              {company.industry && <span className="flex items-center gap-1"><span className="font-mono uppercase text-[9px]">industry</span> {company.industry}</span>}
              {company.country && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {company.country}</span>}
              {company.timezone && <span className="flex items-center gap-1"><Globe2 className="h-3 w-3" /> {company.timezone}</span>}
              {company.website && <a href={company.website} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-emerald-brand hover:underline"><ExternalLink className="h-3 w-3" /> {company.website}</a>}
              {company.primary_email && <span className="flex items-center gap-1"><Mail className="h-3 w-3" /> {company.primary_email}</span>}
            </div>
          </div>
        </div>

        {/* Standard fields (copyable) */}
        <section>
          <h3 className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-3">Company profile</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            <CopyField label="Legal name" value={company.legal_name} testid="cf-legal-name" />
            <CopyField label="Trading name" value={company.trading_name} testid="cf-trading-name" />
            <CopyField label="Primary email" value={company.primary_email} testid="cf-primary-email" />
            <CopyField label="Phone" value={company.phone} testid="cf-phone" />
            <CopyField label="Website" value={company.website} testid="cf-website" />
            <CopyField label="Industry" value={company.industry} testid="cf-industry" />
            <CopyField label="Country" value={company.country} testid="cf-country" />
            <CopyField label="Timezone" value={company.timezone} testid="cf-timezone" />
            <CopyField label="Plan tier" value={company.tier} testid="cf-plan" />
            <CopyField label="Tax ID" value={company.tax_id} testid="cf-tax-id" />
            <CopyField label="Registration number" value={company.registration_number} testid="cf-reg-number" />
            <CopyField label="Company ID" value={company.id} testid="cf-id" />
          </div>
        </section>

        {/* Custom fields (copyable) */}
        <section>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Custom fields</h3>
            <span className="text-[10px] font-mono text-muted-foreground">{cfKeys.length} field{cfKeys.length === 1 ? "" : "s"}</span>
          </div>
          {cfKeys.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground" data-testid="no-custom-fields">
              No custom fields yet. Click <span className="font-mono">Edit</span> above to add key/value metadata.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
              {cfKeys.sort().map((k) => (
                <CopyField key={k} label={k} value={typeof cf[k] === "string" ? cf[k] : JSON.stringify(cf[k])} testid={`custom-field-${k}`} />
              ))}
            </div>
          )}
        </section>
      </div>

      <CompanyEditor company={company} open={editing} onClose={() => setEditing(false)} onSaved={load} />
    </div>
  );
}
